import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SelectField, TextField } from "@/components/common/Forms";
import { ChipGroup } from "@/components/home/QuoteChips";
import { api, formatApiError } from "@/lib/api";
import { TIMELINES } from "@/data/site";
import { QUOTE_SERVICES, QUOTE_SITES, QUOTE_SIZES, SPACES } from "@/data/spaces";

const EMPTY = { property_type: "", services: [], area_sqft: "", sites_count: "", timeline: "", name: "", phone: "", email: "", company: "", city: "", message: "" };

const StepOne = ({ f, set }) => (
  <div className="space-y-5" data-testid="quick-quote-step-1">
    <SelectField id="qq-space" label="Type of space" required value={f.property_type} onChange={set("property_type")} options={SPACES.map((s) => s.name).concat("Other")} />
    <ChipGroup label="What do you need?" multi options={QUOTE_SERVICES} value={f.services} onChange={set("services")} testid="qq-services" />
    <ChipGroup label="Approx. area" options={QUOTE_SIZES} value={f.area_sqft} onChange={set("area_sqft")} testid="qq-size" />
    <ChipGroup label="Number of sites" options={QUOTE_SITES} value={f.sites_count} onChange={set("sites_count")} testid="qq-sites" />
    <ChipGroup label="When do you need it?" options={TIMELINES} value={f.timeline} onChange={set("timeline")} testid="qq-timeline" />
  </div>
);

const StepTwo = ({ f, set }) => (
  <div className="grid gap-4 sm:grid-cols-2" data-testid="quick-quote-step-2">
    <TextField id="qq-name" label="Your name" required value={f.name} onChange={set("name")} autoComplete="name" />
    <TextField id="qq-phone" label="Mobile / WhatsApp" required type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" />
    <TextField id="qq-email" label="Email" required type="email" value={f.email} onChange={set("email")} autoComplete="email" />
    <TextField id="qq-company" label="Organisation" required value={f.company} onChange={set("company")} autoComplete="organization" />
    <TextField id="qq-city" label="City" required value={f.city} onChange={set("city")} className="sm:col-span-2" />
    <TextField id="qq-message" label="Anything else? (optional)" value={f.message} onChange={set("message")} className="sm:col-span-2" placeholder="e.g. 3 floors, basement parking, existing system needs repair" />
  </div>
);

const Done = ({ reference }) => (
  <div className="py-6 text-center" data-testid="quick-quote-success">
    <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
    <p className="mt-4 text-xl font-bold text-slate-900">Quote request received</p>
    <p className="mt-2 text-sm text-slate-600">Our team will call you within 1 business day.</p>
    <p className="mt-4 inline-block rounded-md bg-slate-100 px-4 py-2 text-sm">Reference <span className="mono font-semibold" data-testid="quick-quote-reference">{reference}</span></p>
  </div>
);

const step1Missing = (f) => !f.property_type || !f.services.length || !f.area_sqft || !f.sites_count || !f.timeline;

export const QuickQuote = ({ open, onOpenChange, space }) => {
  const [f, setF] = useState(EMPTY);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) { setF((p) => ({ ...p, property_type: space || p.property_type })); setStep(1); setError(""); setReference(""); }
  }, [open, space]);

  const next = () => (step1Missing(f) ? setError("Please answer each question — one tap each.") : (setError(""), setStep(2)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/enquiries", { kind: "quote", subject: `Quote: ${f.property_type}`, ...f });
      setReference(data.reference);
      setF(EMPTY);
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="quick-quote-dialog">
        {reference ? <Done reference={reference} /> : (
          <form onSubmit={submit} noValidate={step === 1}>
            <DialogHeader>
              <p className="mono text-[11px] uppercase tracking-[0.2em] text-[#C20500]">Step {step} of 2 · under 60 seconds</p>
              <DialogTitle className="text-2xl">{step === 1 ? "Tell us about your space" : "Where should we send your quote?"}</DialogTitle>
              <DialogDescription>{step === 1 ? "Tap to choose — no typing needed." : "We'll call you with a tailored quote. No spam."}</DialogDescription>
            </DialogHeader>
            <div className="mt-6">{step === 1 ? <StepOne f={f} set={set} /> : <StepTwo f={f} set={set} />}</div>
            {error && <p className="mt-4 text-sm text-[#C20500]" data-testid="quick-quote-error">{error}</p>}
            <div className="mt-6 flex items-center justify-between gap-3">
              {step === 2 ? <button type="button" onClick={() => setStep(1)} className="btn-outline" data-testid="quick-quote-back"><ArrowLeft className="h-4 w-4" /> Back</button> : <span />}
              {step === 1
                ? <button type="button" onClick={next} className="btn-primary px-6" data-testid="quick-quote-next">Continue <ArrowRight className="h-4 w-4" /></button>
                : <button type="submit" disabled={busy} className="btn-primary px-6" data-testid="quick-quote-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Get my quote</button>}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
